import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

import { correlationIdInterceptor } from '@altirllc/shared/utils/api/common/interceptors';
import { identity } from '@altirllc/shared/utils/functionalUtils';

import {
  createSynchronizedRefreshAccessToken,
  tryRefreshAndPersistToken,
} from '@/utils/refreshTokenUtils';

import { gatewayInterceptor, createRefreshTokenInterceptor } from './interceptors';

type TRetryConfig = AxiosRequestConfig & { _retry?: boolean };

export const createPrivateApiInterceptors = () => {
  const refreshToken = createSynchronizedRefreshAccessToken(tryRefreshAndPersistToken);

  return {
    apply(axiosInstance: AxiosInstance) {
      const refreshTokenInterceptor = createRefreshTokenInterceptor(axiosInstance, refreshToken);

      const refreshOnceInterceptor = (error: unknown) => {
        const config = (error as AxiosError)?.config as TRetryConfig | undefined;
        // eslint-disable-next-line no-underscore-dangle -- `_retry` is the conventional axios retry-flag name on TRetryConfig
        if (config?._retry) {
          return Promise.reject(error);
        }
        if (config) {
          // eslint-disable-next-line no-underscore-dangle -- `_retry` is the conventional axios retry-flag name on TRetryConfig
          config._retry = true;
        }
        return refreshTokenInterceptor(error);
      };

      axiosInstance.interceptors.request.use(gatewayInterceptor);
      axiosInstance.interceptors.request.use(correlationIdInterceptor);
      axiosInstance.interceptors.response.use(identity, refreshOnceInterceptor);
    },
  };
};
